import { useToken } from "@gluestack-style/react";
import Section from "components/general/Section";
import { Box, Button, Checkbox, FormattedDate, H3, HStack, Link, ParagraphSmall, Text } from "components/ui";
import { CheckCircle, MapPin, Tractor } from "lucide-react-native";
import React, { useState, useCallback, useEffect } from "react";
import { Pressable } from "react-native";
import { Col, Container as GridContainer, Row } from "react-native-flex-grid";
import { truncateText } from "utils/globalFunctions";
import { useIsMobile } from "utils/hooks/useIsMobile";

type Props = {
	dataList: any[];
	selectable?: boolean;
	selectedEquipment?: string[];
	onSelectionChange?: (ids: string[]) => void;
};

const EquipmentTable = ({ dataList, selectable = false, selectedEquipment = [], onSelectionChange }: Props) => {
	const isMobile = useIsMobile();
	const resolvedPrimarylight = useToken("colors", "primarylight");
	const successColor = useToken("colors", "successmain");

	const [selectedIds, setSelectedIds] = useState<string[]>(selectedEquipment);
	const [hoveredId, setHoveredId] = useState<string | null>(null);

	// Sync selectedIds with selectedEquipment prop when it changes
	useEffect(() => {
		setSelectedIds(selectedEquipment);
	}, [selectedEquipment]);

	const handleToggleSelection = useCallback(
		(id: string) => {
			setSelectedIds((prev) => {
				const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
				onSelectionChange?.(next);
				return next;
			});
		},
		[onSelectionChange],
	);

	const handleHoverIn = useCallback((id: string) => {
		setHoveredId(id);
	}, []);

	const handleHoverOut = useCallback(() => {
		setHoveredId(null);
	}, []);

	return (
		<GridContainer
			noPadding
			style={{
				marginLeft: 0,
				maxWidth: "100%",
				marginBottom: 30,
			}}
		>
			{dataList.length === 0 && (
				<Section gap={24}>
					<Box flex={2} gap={12}>
						<H3>No equipment found</H3>
						<ParagraphSmall sx={{ lineHeight: 21 }} weight="normal">
							This page keeps track of all purchased equipment — acting as your team's asset register.
						</ParagraphSmall>
						<ParagraphSmall sx={{ lineHeight: 21 }} weight="normal">
							Right now, it's empty — once purchases are made, everything will appear here automatically.
						</ParagraphSmall>
					</Box>
					<Box flex={1} width="100%">
						<Button isLink borderRadius={8} variant="primary-light" href="/equipment">
							<ParagraphSmall sx={{ color: "$basicwhite" }}>Browse Equipment</ParagraphSmall>
						</Button>
					</Box>
				</Section>
			)}

			{!isMobile && dataList.length > 0 && (
				<Row
					style={{
						marginLeft: 0,
						marginRight: 0,
						marginBottom: 14,
						width: "100%",
						paddingHorizontal: 16,
					}}
				>
					{selectable && <Col style={{ maxWidth: 40 }} />}
					<Col>
						<Text>Serial No</Text>
					</Col>
					<Col>
						<Text>Site</Text>
					</Col>
					<Col>
						<Text>Machine Type</Text>
					</Col>
					<Col>
						<Text>Service Level</Text>
					</Col>
					<Col>
						<Text>Service Due</Text>
					</Col>
				</Row>
			)}

			{dataList.map((equipment: any) => {
				const isSelected = selectedIds.includes(equipment.id);
				const isHovered = hoveredId === equipment.id;

				const RowContent = (
					<Row style={{ width: "100%" }}>
						{selectable && (
							<Col style={{ maxWidth: 40, justifyContent: "center", alignItems: "center" }}>
								{isSelected ? <CheckCircle size={20} color={successColor} /> : <Box width={20} height={20} borderWidth={2} borderColor="$neutral300" borderRadius={4} bg="$basicwhite" />}
							</Col>
						)}
						<Col style={{ justifyContent: "center" }}>
							<HStack gap={8} alignItems="center">
								<Tractor color={resolvedPrimarylight} />
								<Text>{equipment.serialNumber}</Text>
							</HStack>
						</Col>
						<Col style={{ justifyContent: "center" }}>
							<HStack gap={8}>
								<MapPin size={24} color={equipment.siteName ? resolvedPrimarylight : "#999999"} />
								<Text>{truncateText(equipment.siteName || "Not available", 25)}</Text>
							</HStack>
						</Col>
						<Col style={{ justifyContent: "center" }}>
							<Text>
								{equipment.make} - {equipment.type}
							</Text>
						</Col>
						<Col style={{ justifyContent: "center" }}>
							<Text>{equipment.warrantyPeriod ?? "No Plan"}</Text>
						</Col>
						<Col style={{ justifyContent: "center" }}>
							<FormattedDate variant="DD/MM/YYYY" date={equipment.nextServiceDueDate} />
						</Col>
					</Row>
				);

				if (selectable) {
					return (
						<Pressable key={equipment.id} onPress={() => handleToggleSelection(equipment.id)} onHoverIn={() => handleHoverIn(equipment.id)} onHoverOut={handleHoverOut}>
							<EquipmentRowWrapper isSelected={isSelected} isHovered={isHovered}>
								{RowContent}
							</EquipmentRowWrapper>
						</Pressable>
					);
				}

				return <Link key={equipment.id} href={`/equipment/my-equipment/${equipment.slug}`} component={(hovered: boolean) => <EquipmentRowWrapper isHovered={hovered}>{RowContent}</EquipmentRowWrapper>} />;
			})}
		</GridContainer>
	);
};

export default EquipmentTable;

type EquipmentRowWrapperProps = {
	children: React.ReactNode;
	isSelected?: boolean;
	isHovered?: boolean;
};

const EquipmentRowWrapper = React.memo(({ children, isSelected = false, isHovered = false }: EquipmentRowWrapperProps) => {
	const hoverColor = "#E1E9F0";
	const selectedColor = "#D6EAF8";

	const backgroundColor = isSelected ? selectedColor : isHovered ? hoverColor : "white";

	return (
		<Box
			style={{
				marginLeft: 0,
				marginRight: 0,
				paddingHorizontal: 16,
				paddingVertical: 18,
				borderRadius: 8,
				borderWidth: 1,
				marginBottom: 14,
				borderColor: "#E1E9F0",
				backgroundColor,
			}}
		>
			{children}
		</Box>
	);
});

EquipmentRowWrapper.displayName = "EquipmentRowWrapper";
